import { useMemo, useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Button } from "@/components/ui/button"
import { TriangleAlert } from "lucide-react"
import { TruncatedCell } from "@/components/shared/TruncatedCell"
import TableFiltersSelector, {
  type FilterOption,
  type RangeValue,
} from "@/components/shared/TableFiltersSelector"
import {
  POLYGON_COLUMNS,
  POLYGON_COLUMN_LABELS,
  VALIDATION_ERROR_COLUMN,
  VALIDATION_ERROR_LABEL,
  type PolygonRow,
  type PolygonFailureRow,
} from "@/services/prPolygonService"
import VersionSelector from "./VersionSelector"

/** Number of rows shown per page. */
const ROWS_PER_PAGE: number = 50

/**
 * Columns excluded from the grid.
 */
const HIDDEN_COLUMNS: ReadonlySet<keyof PolygonRow> = new Set(["shapefile_s3_uri"])

/** The ordered columns actually rendered in the table (hidden ones removed). */
const VISIBLE_COLUMNS: readonly (keyof PolygonRow)[] =
  POLYGON_COLUMNS.filter((column) => !HIDDEN_COLUMNS.has(column))

/**
 * Categorical fields exposed in the filter dialog, in tab order. Each becomes a
 * multi-select whose options are the distinct values present in the data.
 */
const CATEGORICAL_FILTER_FIELDS: readonly (keyof PolygonRow)[] = [
  "biblio_id",
  "iso3",
  "method",
]

/**
 * Filter key for the survey-year range tab in the filter dialog. Not a single
 * column — it constrains year_start/year_end together via interval overlap.
 */
const YEAR_RANGE_KEY: string = "year_range"

/** Props for {@link PrPolygonsTable}. */
interface PrPolygonsTableProps {
  /**
   * The full set of rows to display. In failure mode these are
   * {@link PolygonFailureRow} (carrying the extra `validation_error` column);
   * otherwise plain {@link PolygonRow}.
   */
  rows: (PolygonRow | PolygonFailureRow)[]
  /** All selectable dataset versions (within the supported major), newest first. */
  versions: string[]
  /** The currently loaded dataset version. */
  currentVersion: string
  /** Called when the user picks a different version from the dropdown. */
  onVersionChange: (version: string) => void
  /** Whether the grid is currently showing validation failures. */
  failureMode: boolean
  /** Called when the user toggles the failures view on or off. */
  onToggleFailureMode: () => void
  /**
   * The active categorical filters (field → selected values). Controlled by the
   * parent so the selection can be mirrored into the URL and restored from it.
   */
  categoricalFilters: Map<string, Set<string>>
  /** The active survey-year range, or null when not narrowed. Controlled by the parent. */
  yearRange: RangeValue | null
  /**
   * Called when the user commits new filters from the dialog. The parent owns
   * the filter state (and the URL mirror); this component only renders it.
   *
   * @param filters - The field → selected-values map from the dialog.
   * @param yearRange - The survey-year range, or null when both bounds cleared.
   */
  onFiltersChange: (filters: Map<string, Set<string>>, yearRange: RangeValue | null) => void
}

/**
 * Render a cell value for display.
 *
 * Numbers are rendered with up to 4 decimals (trailing zeros trimmed)
 *
 * @param value - The raw cell value.
 * @returns The display string, or null when the value is absent.
 */
function formatCellValue(value: string | number | null): string | null {
  if (value == null) {
    return null
  }
  if (typeof value === "number") {
    return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(4)))
  }
  return value
}

/**
 * Resolve the display header for a column, falling back to the raw name when no
 * friendly label is defined.
 *
 * @param column - The column key (a PolygonRow key or `validation_error`).
 * @returns The header text to show.
 */
function columnLabel(column: string): string {
  if (column === VALIDATION_ERROR_COLUMN) {
    return VALIDATION_ERROR_LABEL
  }
  return POLYGON_COLUMN_LABELS[column as keyof PolygonRow] ?? column
}

/**
 * Scrollable, filterable, paginated grid of PR-by-polygon rows.
 *
 * All filtering and pagination run client-side over the already-loaded dataset.
 *
 * @param props - Component props.
 * @param props.rows - The full set of polygon rows to display.
 * @returns The rendered table with filter toolbar and pagination controls.
 */
export default function PrPolygonsTable({
  rows,
  versions,
  currentVersion,
  onVersionChange,
  failureMode,
  onToggleFailureMode,
  categoricalFilters,
  yearRange,
  onFiltersChange,
}: PrPolygonsTableProps): React.JSX.Element {
  // The columns to render: the normal visible columns, plus the
  // failures-only validation_error column appended when in failure mode.
  const displayColumns: readonly string[] = useMemo(
    (): readonly string[] =>
      failureMode
        ? [...VISIBLE_COLUMNS.map(String), VALIDATION_ERROR_COLUMN]
        : VISIBLE_COLUMNS.map(String),
    [failureMode],
  )

  const [page, setPage] = useState<number>(0)

  // Adapt the single controlled yearRange into the Map keyed by filter key that
  // TableFiltersSelector expects for its initial range values.
  const initialRanges: Map<string, RangeValue> = useMemo(() => {
    const map = new Map<string, RangeValue>()
    if (yearRange != null && (yearRange.from != null || yearRange.to != null)) {
      map.set(YEAR_RANGE_KEY, yearRange)
    }
    return map
  }, [yearRange])

  /**
   * Filter definitions for the dialog: one per categorical field, each with its
   * distinct non-null values sorted ascending. Recomputed only when rows change.
   */
  const filterOptions: FilterOption[] = useMemo(() => {
    const categorical: FilterOption[] = CATEGORICAL_FILTER_FIELDS.map((field) => {
      const distinct: Set<string> = new Set()
      for (const row of rows) {
        const value: string | number | null = row[field]
        if (value != null && String(value).trim() !== "") {
          distinct.add(String(value))
        }
      }
      return {
        key: field,
        label: columnLabel(field),
        type: "multiselect" as const,
        // Sort numerically when the values look numeric (e.g. biblio ids),
        // otherwise alphabetically.
        options: Array.from(distinct).sort((a, b) => {
          const na: number = Number(a)
          const nb: number = Number(b)
          if (Number.isFinite(na) && Number.isFinite(nb)) {
            return na - nb
          }
          return a.localeCompare(b)
        }),
      }
    })

    // Survey-year range tab (overlap filter on year_start/year_end).
    const yearRange: FilterOption = {
      key: YEAR_RANGE_KEY,
      label: "Year",
      type: "range",
      fromPlaceholder: "From year",
      toPlaceholder: "To year",
    }

    return [...categorical, yearRange]
  }, [rows])

  /**
   * Rows passing every active filter. Categorical fields AND together (OR within
   * a field); the year range uses interval overlap. Recomputed when the rows or
   * any filter changes.
   */
  const filteredRows: (PolygonRow | PolygonFailureRow)[] = useMemo(() => {
    const yearFrom: number | null = yearRange?.from ?? null
    const yearTo: number | null = yearRange?.to ?? null

    return rows.filter((row) => {
      // Categorical: every field with a selection must match one of its values.
      for (const [field, selected] of categoricalFilters) {
        if (selected.size === 0) {
          continue
        }
        const value: string | number | null = row[field as keyof PolygonRow]
        if (value == null || !selected.has(String(value))) {
          return false
        }
      }

      // Year overlap: keep when [year_start, year_end] overlaps [from, to].
      // A missing end is treated as the start (single-year survey); a row with
      // no year data at all is never excluded by the year filter.
      if (yearFrom != null || yearTo != null) {
        const start: number | null = row.year_start
        const end: number | null = row.year_end ?? row.year_start
        if (start != null && end != null) {
          const windowStart: number = yearFrom ?? -Infinity
          const windowEnd: number = yearTo ?? Infinity
          const overlaps: boolean = start <= windowEnd && end >= windowStart
          if (!overlaps) {
            return false
          }
        }
      }

      return true
    })
  }, [rows, categoricalFilters, yearRange])

  const totalPages: number = Math.max(1, Math.ceil(filteredRows.length / ROWS_PER_PAGE))
  // Clamp the current page so a shrinking result set never leaves us past the end.
  const safePage: number = Math.min(page, totalPages - 1)
  const pageStart: number = safePage * ROWS_PER_PAGE
  const pageRows: (PolygonRow | PolygonFailureRow)[] = filteredRows.slice(pageStart, pageStart + ROWS_PER_PAGE)

  const hasActiveFilters: boolean =
    categoricalFilters.size > 0 || yearRange?.from != null || yearRange?.to != null

  /**
   * Commit the filters chosen in the dialog (both categorical and range) up to
   * the parent and reset to page one.
   *
   * @param filters - The field -> selected-values map from the dialog.
   * @param ranges - The range filters keyed by filter key (year here).
   */
  function handleFiltersSave(
    filters: Map<string, Set<string>>,
    ranges?: Map<string, RangeValue>,
  ): void {
    const nextYearRange: RangeValue | null = ranges?.get(YEAR_RANGE_KEY) ?? null
    onFiltersChange(filters, nextYearRange)
    setPage(0)
  }

  return (
    <div className="flex flex-1 flex-col min-h-0 gap-3">
      {/* Toolbar: filters + version selector + result count */}
      <div className="flex flex-wrap items-center gap-2">
        <TableFiltersSelector
          filterOptions={filterOptions}
          initialFilters={categoricalFilters}
          initialRanges={initialRanges}
          onSave={handleFiltersSave}
        />

        <Button
          type="button"
          size="sm"
          variant={failureMode ? "destructive" : "outline"}
          className="h-8 text-xs"
          aria-pressed={failureMode}
          onClick={onToggleFailureMode}
        >
          <TriangleAlert className="size-3.5" aria-hidden="true" />
          {failureMode ? "Showing failures" : "Show failures"}
        </Button>

        <VersionSelector
          currentVersion={currentVersion}
          versions={versions}
          onVersionChange={onVersionChange}
          side="bottom"
        />

        <p className="ml-auto text-xs text-muted-foreground">
          {filteredRows.length.toLocaleString()}
          {hasActiveFilters ? ` of ${rows.length.toLocaleString()}` : ""}
          {failureMode ? " failing rows" : " rows"}
        </p>
      </div>

      {/* Scrollable table region */}
      <div className="flex-1 min-h-0 overflow-auto rounded-md border">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-background">
            <TableRow>
              {displayColumns.map((column) => (
                <TableHead key={column} scope="col" className="whitespace-nowrap">
                  {columnLabel(column)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={displayColumns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {failureMode
                    ? hasActiveFilters
                      ? "No validation failures match the current filters."
                      : "No validation failures for this version."
                    : hasActiveFilters
                      ? "No rows match the current filters."
                      : "No rows in this dataset."}
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row, rowIndex) => (
                <TableRow key={pageStart + rowIndex}>
                  {displayColumns.map((column) => (
                    <TableCell key={column} className="max-w-[16rem]">
                      <TruncatedCell
                        value={formatCellValue(
                          (row as Record<string, string | number | null>)[column] ?? null,
                        )}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination controls — only shown when there is more than one page */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            Page {safePage + 1} of {totalPages}
          </p>
          <Pagination className="mx-0 w-auto justify-end">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  aria-disabled={safePage === 0}
                  className={safePage === 0 ? "pointer-events-none opacity-50" : undefined}
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  aria-disabled={safePage >= totalPages - 1}
                  className={safePage >= totalPages - 1 ? "pointer-events-none opacity-50" : undefined}
                  onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  )
}
